<script setup lang="ts">
import { ClockIcon, UsersIcon, FireIcon } from "@heroicons/vue/24/solid";
import { ClockIcon as ClockIconOutline } from "@heroicons/vue/24/outline";
import { useLazyAsyncData, useNuxtApp } from "#app";
import { computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useRecipeDetailStore } from "~/stores/recipe/recipeDetail";
import { ResizeType } from "@bitpatty/imgproxy-url-builder";
import { clearAdsenseBlocks } from "~/utils/adsense";

const nuxtApp = useNuxtApp();
const recipeDetailStore = useRecipeDetailStore();
const slug = String(useRoute().params?.slug);

await useLazyAsyncData('recipeDetailStore', async () => recipeDetailStore.fetch(slug));
if (typeof recipeDetailStore.error === "object") {
    throw recipeDetailStore.error;
}

const ingredientGroups = computed(() => recipeDetailStore.ingredientGroups);
const categorySlug = computed(() => recipeDetailStore.category?.slug);
const text = computed(() => recipeDetailStore.text ? useNuxtApp().marked().parse(recipeDetailStore.text) : recipeDetailStore.text);
const stepsWithText = computed(() => recipeDetailStore.stepsWithText ? useNuxtApp().marked().parse(recipeDetailStore.stepsWithText) : recipeDetailStore.stepsWithText);
const portions = computed(() => recipeDetailStore.portions);
const prepTime = computed(() => recipeDetailStore.getPrepTime);
const cookingTime = computed(() => recipeDetailStore.getCookingTime);
const totalTime = computed(() => recipeDetailStore.getTotalTime);
const cuisineTitle = computed(() => recipeDetailStore.countryOfOrigin?.title);
const cuisineFlag = computed(() => recipeDetailStore.countryOfOrigin?.flag);
const calories = computed(() => recipeDetailStore.calories);

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    recipeDetailStore.clear();
});
</script>

<template>
    <PartFancybox only-js-lib />
    <section class="recipe-detail-hero text-center container lg:text-left">
        <div class="w-full mx-auto sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-7xl xl:px-32">
            <div class="grid items-center lg:grid-cols-2 space-y-12 lg:space-y-0">
                <div>
                    <div class="lg:rounded-lg lg:bg-[hsla(0,0%,100%,0.55)] lg:px-6 lg:py-12 lg:shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] md:px-12 lg:-mr-14 lg:backdrop-blur-[30px]">
                        <h1 v-if="recipeDetailStore.title !== undefined"
                            class="text-4xl font-bold tracking-tight md:text-5xl xl:text-6xl">
                            {{ recipeDetailStore.title }}
                        </h1>
                        <h1 v-else class="text-4xl font-bold tracking-tight md:text-5xl xl:text-6xl">
                            <span class="animate-pulse rounded block h-10 w-[80%] bg-gray-100 mx-auto lg:mx-0"></span>
                            <span class="animate-pulse rounded block h-10 w-[95%] mt-3 bg-gray-100 mx-auto lg:mx-0"></span>
                            <span class="animate-pulse rounded block h-10 w-[60%] mt-3 bg-gray-100 mx-auto lg:mx-0"></span>
                        </h1>
                        <div class="mt-6">
                            <nuxt-link v-if="recipeDetailStore.title !== undefined && recipeDetailStore?.category?.title"
                                       :to="recipeDetailStore.category?.url" class="font-semibold text-indigo-800">
                                {{ recipeDetailStore.category?.title }} <span aria-hidden="true">&rarr;</span>
                            </nuxt-link>
                            <span v-else-if="recipeDetailStore.title === undefined"
                                  class="animate-pulse rounded block h-5 w-[40%] bg-indigo-50 mx-auto lg:mx-0"></span>
                        </div>
                        <template v-if="recipeDetailStore.title !== undefined">
                            <div v-if="totalTime || portions"
                                 class="recipe-features w-[17rem] mx-auto mt-6 grid grid-cols-2 gap-4 sm:w-auto sm:mx-0 sm:gap-0 sm:grid-cols-0 sm:flex sm:space-x-12">
                                <div v-if="totalTime" class="sm:grow my-auto">
<!--                                    <span class="uppercase text-xs">Time</span>-->
                                    <span v-if="prepTime" class="flex items-center text-sm">
                                        <span class="w-6 mr-1">
                                            <ClockIconOutline class="h-3 w-3 mx-auto text-indigo-600"/>
                                        </span>
                                        <span class="relative">
                                            <span class="absolute -right-[2rem] -top-[0.2rem] text-xs font-medium text-indigo-800">prep</span>
                                            {{ prepTime }}
                                        </span>
                                    </span>

                                    <span v-if="cookingTime" class="flex items-center text-md">
                                        <span class="w-6 mr-1">
                                            <ClockIconOutline class="h-4 w-4 mx-auto text-indigo-600"/>
                                        </span>
                                        <span class="relative">
                                            <span class="absolute -right-[2rem] -top-[0.2rem] text-xs font-medium text-indigo-800">cook</span>
                                            {{ cookingTime }}
                                        </span>
                                    </span>
                                    <span class="flex items-center text-lg font-semibold">
                                        <span class="w-6 mr-1">
                                            <ClockIcon class="h-6 w-6 mx-auto text-indigo-600"/>
                                        </span>
                                        {{ totalTime }}
                                    </span>
                                </div>
                                <div v-if="portions" class="my-auto">
                                    <span class="uppercase text-xs">Portions</span>
                                    <span class="flex items-center text-lg font-semibold h-8">
                                        <UsersIcon class="h-6 w-6 mr-2 text-indigo-600"/> {{
                                            portions
                                        }}
                                    </span>
                                </div>
                                <div v-if="calories" class="my-auto">
                                    <span class="uppercase text-xs">Calories</span>
                                    <span class="flex items-center text-lg font-semibold h-8">
                                        <FireIcon class="h-6 w-6 mr-2 text-red-600"/> {{
                                            calories
                                        }}
                                    </span>
                                </div>
                                <div v-if="cuisineFlag" class="my-auto">
                                    <span class="uppercase text-xs">Cuisine</span>
                                    <span class="flex items-center text-3xl h-8" :title="cuisineTitle">
                                        {{ cuisineFlag }}
                                    </span>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="mt-6 flex mt-10 -mx-6 justify-center lg:justify-normal">
                                <div class="px-6">
                                    <span class="animate-pulse rounded block h-5 w-[5rem] bg-gray-100"></span>
                                    <span class="animate-pulse rounded block h-5 w-[5rem] bg-indigo-50 mt-2"></span>
                                </div>
                                <div class="px-6 border-l-[1px]">
                                    <span class="animate-pulse rounded block h-5 w-[5rem] bg-gray-100"></span>
                                    <span class="animate-pulse rounded block h-5 w-[5rem] bg-indigo-50 mt-2"></span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
                <div>
                    <picture v-if="recipeDetailStore.title !== undefined">
                        <source media="(max-width: 766px)" type="image/webp" :srcset="`${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 300, 300, 'webp', ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 600, 600, 'webp', ResizeType.FILL)} 2x`">
                        <source media="(max-width: 766px)" :srcset="`${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 300, 300, undefined, ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 600, 600, undefined, ResizeType.FILL)} 2x`">
                        <source media="(min-width: 767px)" type="image/webp" :srcset="`${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 512, 512, 'webp', ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 1024, 1024, 'webp', ResizeType.FILL)} 2x`">
                        <source media="(min-width: 767px)" :srcset="`${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 512, 512, undefined, ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 1024, 1024, undefined, ResizeType.FILL)} 2x`">
                        <img width="512"
                             height="512"
                             :src="nuxtApp.imgProxyResize(recipeDetailStore.mainPicture, 300, 300, undefined, ResizeType.FILL)"
                             class="w-full rounded-lg shadow-lg bg-gray-100"
                             :alt="recipeDetailStore.title"
                        />
                    </picture>
                    <picture v-else>
                        <div class="w-full h-[30rem] rounded-lg shadow-lg bg-gray-100">
                            <div class="flex flex-row items-center h-full w-full">
                                <div class="w-full">
                                    <span class="animate-pulse rounded block h-5 w-[50%] mx-auto bg-gray-200"></span>
                                    <span class="animate-pulse rounded block h-5 w-[30%] mx-auto my-5 bg-gray-200"></span>
                                    <span class="animate-pulse rounded block h-5 w-[50%] mx-auto bg-gray-200"></span>
                                </div>
                            </div>
                        </div>
                    </picture>
                </div>
            </div>
        </div>
    </section>

    <template v-if="recipeDetailStore.title !== undefined">
        <section v-if="recipeDetailStore.text" class="container mx-auto">
            <div class="prose md:w-3/5 mx-auto text-lg sm:text-xl md:text-center font-medium"
                 v-html="text"></div>
        </section>
    </template>
    <template v-else>
        <section class="container mx-auto">
            <div class="md:w-3/5 mx-auto text-xl md:text-center font-medium">
                <span class="animate-pulse rounded inline-block h-5 w-[80%] bg-gray-100"></span>
                <span class="animate-pulse rounded inline-block h-5 w-[70%] bg-gray-100"></span>
                <span class="animate-pulse rounded inline-block h-5 w-[60%] bg-gray-100"></span>
            </div>
        </section>
    </template>

<!--    <ClientOnly>
        <section class="hidden sm:flex container justify-center">
            <AdsenseMediaBigWidth/>
        </section>
        <section class="sm:hidden container flex justify-center">
            <AdsenseMedia/>
        </section>
    </ClientOnly>-->

    <section v-if="recipeDetailStore.title !== undefined && ingredientGroups !== undefined && ingredientGroups.length"
             class="text-gray-600 body-font"
    >
        <div v-if="ingredientGroups !== undefined" class="container mx-auto space-y-6 sm:space-y-12">
            <h2 class="text-center sm:text-3xl text-2xl font-bold title-font text-gray-900">Ingredients</h2>
            <div class="md:flex items-start justify-center">
                <div v-for="(ingredientGroup, index) in ingredientGroups"
                     :key="index"
                     class="p-4"
                >
                    <h2 v-if="ingredientGroup.title"
                        class="font-bold tracking-widest mb-4 text-sm sm:text-left uppercase sm:initial"
                    >{{ ingredientGroup.title }}</h2>
<!--                    <nav class="hidden sm:flex flex-col sm:items-start -mb-1 space-y-2.5">
                        <span v-for="(ingredients, ingIndex) in ingredientGroup.ingredients" :key="ingIndex" class="flex items-center">
                            <div><span class="bg-indigo-600 w-4 h-4 mr-2 rounded-full block"></span>
                            </div>{{ ingredients.title }} <span v-if="ingredients.dosage">&mdash;</span><span v-if="ingredients.dosage" class="font-medium">&nbsp;{{ ingredients.dosage }}</span>
                        </span>
                    </nav>-->
                    <ul class="list-disc list-inside space-y-2 sm:space-y-4">
                        <li v-for="(ingredients, ingIndex) in ingredientGroup.ingredients" :key="ingIndex" class="text-indigo-600 text-lg">
                            <span class="text-gray-600 text-base">{{ ingredients.title }} <span v-if="ingredients.dosage">&mdash;</span><span v-if="ingredients.dosage" class="font-medium">&nbsp;{{ ingredients.dosage }}</span></span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </section>
    <section v-else-if="recipeDetailStore.title === undefined && ingredientGroups === undefined" class="text-gray-600 body-font">
        <div class="container mx-auto">
            <span class="animate-pulse rounded block h-5 w-[15rem] bg-gray-100 mx-auto"></span>
            <div class="sm:flex -m-4 items-start justify-center">
                <div v-for="index in 3" :key="index"
                     class="p-4 w-full sm:w-1/2 lg:w-1/3"
                >
                        <span class="mb-4 animate-pulse rounded block h-3 w-[10rem] bg-gray-100"
                        ></span>
                    <nav
                        class="flex flex-col sm:items-start -mb-1 space-y-2.5">
                        <span class="animate-pulse rounded inline-block h-5 w-[40%] bg-gray-100"></span>
                        <span class="animate-pulse rounded inline-block h-5 w-[30%] bg-gray-100"></span>
                        <span class="animate-pulse rounded inline-block h-5 w-[60%] bg-gray-100"></span>
                        <span class="animate-pulse rounded inline-block h-5 w-[80%] bg-gray-100"></span>
                    </nav>
                </div>
            </div>
        </div>
    </section>

<!--    <ClientOnly>
        <section class="flex container justify-center">
            <AdsenseSmallHorizontal/>
        </section>
    </ClientOnly>-->

    <template v-if="recipeDetailStore.title !== undefined">
        <section v-if="recipeDetailStore.steps && recipeDetailStore.steps.length" class="container md:max-w-5xl mx-auto space-y-6 sm:space-y-24">
            <h2 class="text-center sm:text-3xl text-2xl font-bold title-font text-gray-900">Unlock the Secrets of Flavor<br>with Our Step-by-Step<br>Cooking Instructions!</h2>
            <div>
                <template v-for="(step, index) in recipeDetailStore.steps"
                          :key="index"
                >
                    <div v-if="index > 0"
                         class="flex items-start"
                         :class="{'flex-row-reverse': index%2 === 0, 'flex-row': index%2 === 1}"
                    >
                        <div class="border-t-4 border-transparent"
                             :class="{'border-l-4': index%2 === 0, 'border-r-4': index%2 === 1}"
                        >
                            <div class="w-16 h-16 border-indigo-600 border-dashed border-b-4"
                                 :class="{'ml-16 border-l-4 rounded-bl-full': index%2 === 1, 'mr-16 border-r-4 rounded-br-full': index%2 === 0}"
                            ></div>
                        </div>
                        <div class="border-t-4 border-transparent flex-auto">
                            <div class="h-16 border-b-4 border-indigo-600 border-dashed"></div>
                        </div>
                        <div class="w-16 h-16 mt-16 border-indigo-600 border-dashed border-t-4"
                             :class="{'mr-16 border-r-4 rounded-tr-full': index%2 === 1, 'ml-16 border-l-4 rounded-tl-full': index%2 === 0}"
                        ></div>
                    </div>
                    <div class="flex" :class="{'flex-row-reverse': index%2 === 1, 'flex-row': index%2 === 0}">
                        <div class="hidden md:flex flex-col items-center">
                            <div
                                class="w-32 py-5 border border-indigo-600 rounded uppercase flex flex-col items-center justify-center"
                                :class="{'mr-4': index%2 === 0, 'ml-4': index%2 === 1}"
                                :id="`step${index + 1}`"
                            >
                                <div class="text-2xl font-black text-indigo-600">Step {{ index + 1 }}</div>
                            </div>
                            <div class="h-full border-transparent"
                                 :class="{'border-l-4': index%2 === 1, 'border-r-4': index%2 === 0}"
                            >
                                <div class="h-full border-indigo-600 border-dashed"
                                     :class="{'border-l-4 ml-2': index%2 === 1, 'border-r-4 mr-2': index%2 === 0}"
                                ></div>
                            </div>
                        </div>
                        <div class="flex-auto">
                            <div class="flex md:flex-row flex-col items-center">
                                <div class="min-w-full md:min-w-[initial] flex-auto">
                                    <div class="md:hidden text-lg font-normal uppercase pt-3 text-indigo-600"><span
                                        class="font-black">Step {{ index + 1 }}</span></div>
                                    <div class="text-lg md:px-5 prose max-w-full" v-html="useNuxtApp().marked().parse(step.text)"></div>
                                </div>
                                <picture v-if="step.picture">
                                    <source media="(max-width: 766px)" type="image/webp" :srcset="`${nuxtApp.imgProxyResize(step.picture, 200, 200, 'webp', ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(step.picture, 400, 400, 'webp', ResizeType.FILL)} 2x`">
                                    <source media="(max-width: 766px)" :srcset="`${nuxtApp.imgProxyResize(step.picture, 200, 200, undefined, ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(step.picture, 400, 400, undefined, ResizeType.FILL)} 2x`">
                                    <source media="(min-width: 767px)" type="image/webp" :srcset="`${nuxtApp.imgProxyResize(step.picture, 240, 240, 'webp', ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(step.picture, 480, 480, 'webp', ResizeType.FILL)} 2x`">
                                    <source media="(min-width: 767px)" :srcset="`${nuxtApp.imgProxyResize(step.picture, 240, 240, undefined, ResizeType.FILL)} 1x, ${nuxtApp.imgProxyResize(step.picture, 480, 480, undefined, ResizeType.FILL)} 2x`">
                                    <img v-if="step.picture"
                                         loading="lazy"
                                         width="240"
                                         height="240"
                                         :src="nuxtApp.imgProxyResize(step.picture, 240, 240, undefined, ResizeType.FILL)"
                                         :alt="`Step ${index + 1}`"
                                         class="my-5 md:my-0 rounded object-cover min-w-[15rem] w-[15rem] h-[15rem] bg-gray-100">
                                </picture>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </section>
        <section v-if="stepsWithText" class="container md:w-3/5 mx-auto md:mt-6">
            <h2 v-if="!recipeDetailStore.steps || !recipeDetailStore.steps.length"
                class="text-center sm:text-3xl text-2xl font-bold title-font text-gray-900 mb-10 sm:mb-20"
            >Step by step recipe</h2>
            <div
                class="prose max-w-full text-lg"
                :class="{'mt-10 sm:mt-20': !recipeDetailStore.steps || !recipeDetailStore.steps.length}"
                v-html="stepsWithText"
            ></div>
        </section>
    </template>
    <template v-else>
        <section class="container md:max-w-5xl mx-auto py-10 md:my-10">
            <span class="animate-pulse rounded block h-5 w-[20rem] bg-gray-100 mb-20 mx-auto"></span>
            <div>
                <template v-for="(step, index) in 3"
                          :key="index"
                >
                    <div v-if="index > 0"
                         class="flex items-start"
                         :class="{'flex-row-reverse': index%2 === 0, 'flex-row': index%2 === 1}"
                    >
                        <div class="border-t-4 border-transparent"
                             :class="{'border-l-4': index%2 === 0, 'border-r-4': index%2 === 1}"
                        >
                            <div class="w-16 h-16 border-indigo-600 border-dashed border-b-4"
                                 :class="{'ml-16 border-l-4 rounded-bl-full': index%2 === 1, 'mr-16 border-r-4 rounded-br-full': index%2 === 0}"
                            ></div>
                        </div>
                        <div class="border-t-4 border-transparent flex-auto">
                            <div class="h-16 border-b-4 border-indigo-600 border-dashed"></div>
                        </div>
                        <div class="w-16 h-16 mt-16 border-indigo-600 border-dashed border-t-4"
                             :class="{'mr-16 border-r-4 rounded-tr-full': index%2 === 1, 'ml-16 border-l-4 rounded-tl-full': index%2 === 0}"
                        ></div>
                    </div>
                    <div class="flex" :class="{'flex-row-reverse': index%2 === 1, 'flex-row': index%2 === 0}">
                        <div class="hidden md:flex flex-col items-center">
                            <div
                                class="w-32 py-5 border border-indigo-600 rounded uppercase flex flex-col items-center justify-center"
                                :class="{'mr-4': index%2 === 0, 'ml-4': index%2 === 1}"
                            >
                                <div class="text-2xl font-black text-indigo-600"><span
                                    class="animate-pulse rounded block h-7 w-[5rem] bg-indigo-50"></span></div>
                            </div>
                            <div class="h-full border-transparent"
                                 :class="{'border-l-4': index%2 === 1, 'border-r-4': index%2 === 0}"
                            >
                                <div class="h-full border-indigo-600 border-dashed"
                                     :class="{'border-l-4 ml-2': index%2 === 1, 'border-r-4 mr-2': index%2 === 0}"
                                ></div>
                            </div>
                        </div>
                        <div class="flex-auto">
                            <div class="flex md:flex-row flex-col items-center">
                                <div class="min-w-full md:min-w-[initial] flex-auto">
                                    <div class="md:hidden text-lg font-normal uppercase pt-3 text-indigo-600"><span
                                        class="font-black"><span
                                        class="animate-pulse rounded block h-7 w-[5rem] bg-indigo-50 mb-4"></span></span>
                                    </div>
                                    <div class="text-lg md:px-5">
                                        <span class="animate-pulse rounded inline-block h-5 w-[80%] bg-gray-100"></span>
                                        <span class="animate-pulse rounded inline-block h-5 w-[70%] bg-gray-100"></span>
                                        <span class="animate-pulse rounded inline-block h-5 w-[60%] bg-gray-100"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </section>
    </template>

    <ClientOnly>
        <section class="hidden sm:flex container justify-center">
            <AdsenseMediaBigWidth/>
        </section>
        <section class="sm:hidden container flex justify-center">
            <AdsenseSmallHorizontal/>
        </section>
    </ClientOnly>

    <section class="container grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <LastInterestingFactsByCategoryToAside :category-id="2" :page-size="4"/>
        <LastInterestingFactsByCategoryToAside :category-id="4" :page-size="4"/>
        <LastInterestingFactsByCategoryToAside :category-id="3" :page-size="4"/>
        <LastInterestingFactsByCategoryToAside :category-id="1" :page-size="4"/>
    </section>
    <RecommendByCategory v-if="categorySlug !== undefined"
                         :category-slug="categorySlug"
                         page-size="4"
                         title="See what delicious recipes 😋 we've picked out for you!"
    />
</template>